// Owned by the workbench: AIOE mirrors these shapes (mju-aioe/packages/shared/src/schemas/workflows.ts). The workbench never mirrors it back. Keep the two identical.
/**
 * Workflows (D401, 0.90.0; the design is docs/plans/2026-10-workflows.md). A workflow is a picture of work that runs the same
 * way every time: **steps** joined by **branches**. Four kinds of step:
 *
 *   agent      a named specialist or a named member of a team works on it (never an agent started for the step)
 *   foundry    a named agent in a Microsoft Foundry project answers one request (D439): through the workbench's local proxy, so
 *              the Foundry policy, DLP and the usage and audit rows apply, as for any Foundry member. It names the agent by
 *              `agentId` (`<project address>/agents/<name>`) and, optionally, a version; with none it uses the newest
 *   code       proven code does something exact: a built-in action (an HTTP call with a kept key, write a file, open a pull
 *              request, file or comment on a task, tell people, publish a report), a template that puts earlier steps'
 *              answers together, or a script from the repository run in the repository's sandbox
 *   approval   a person decides: approved, rejected or held. Each of the three goes somewhere, so every path is accounted
 *              for, and a timeout holds or rejects, never approves
 *
 * A step with several branches out runs them side by side; a step with several branches in waits for them (`join: "all"`,
 * the default, or the first, `"any"`). A branch not taken (the rejected way out of an approval when it was approved) marks the
 * steps only it leads to as not needed (`skipped`), so a step that joins it still runs on what did arrive.
 *
 * Where a workflow runs: a repository's workflow in that repository's workbench (the host), a project's on the desktop, a
 * node's on the node. AIOE never runs one: it lists them in its catalogue, starts them on a machine with the `workflow.start`
 * device order ({@link workflowStartOrderBodySchema}), keeps the run and step records the machine reports
 * ({@link workflowRunSchema}, {@link workflowStepRunSchema}), files approvals in the Inbox and on the phone, and audits.
 *
 * The run's records are idempotent by id and their states are ordered ({@link WORKFLOW_STATES}, {@link mayAdvance}): a report
 * that arrives late never moves a step back. {@link keepReport} is the rule both sides apply.
 *
 * The approval's Inbox action is the Control API method {@link WORKFLOW_APPROVAL_DECIDE_RPC} on the machine that runs the
 * workflow, with {@link workflowApprovalDecisionSchema} as its params; the organisation relays a phone's answer to it, as it
 * does every Inbox action a machine filed.
 *
 * The catalogue (shaped like apps', shared/schemas/microapps.ts, and routines', shared/schemas/routineCatalogue.ts): your own
 * workflows, the repository's (`.mojoup/workflows/<id>.json`), and versions published to the team's hub or the organisation,
 * listed at once or held for review, immutable, withdrawn by the author or an administrator. The routes at a project's home
 * are `/routines/v1`'s under `/workflows/v1/:projectId` (list, publish, versions, review, withdraw, install), plus
 * `POST /workflows/v1/:projectId/runs` `{ runs[], steps[] }` (upsert by id through {@link keepReport}, at most 500) and
 * `GET /workflows/v1/:projectId/runs?from=&to=&workflowId=`.
 *
 * Words for people: workflow, step, branch, approval, approved, rejected, held, waiting for you, not needed. Never "DAG",
 * "node" for a step, "edge" or "orchestration" on screen.
 *
 * Pure: zod and string helpers only.
 */
import { z } from "zod";
import { agentAnalyticsId, memberKeyOf, specialistAnalyticsId } from "../teams/memberIds.js";
import { routineEventSchema } from "./routineTriggers.js";
export const WORKFLOW_SCHEMA_VERSION = 1;
// ── Limits ─────────────────────────────────────────────────────────────
/** How big a workflow and what it carries may be. Both sides refuse more. */
export const WORKFLOW_LIMITS = {
    /** Steps in one workflow. */
    steps: 50,
    /** Branches in one workflow. */
    edges: 120,
    /** Inputs a run is started with. */
    inputs: 20,
    /** Characters of an agent step's prompt, a template, an approval's brief. */
    prompt: 8_000,
    template: 16_000,
    brief: 4_000,
    /** Characters of code a step runs as written (`run.kind: "code"`). */
    source: 20_000,
    /** Bytes of JSON: a run's inputs, one step's output as recorded and passed on. */
    inputBytes: 32_000,
    outputBytes: 64_000,
    /** Tries after the first. */
    retries: 5,
    /** Minutes: the longest an agent step, a code step and an approval may take. */
    agentMinutes: 24 * 60,
    codeMinutes: 60,
    /** Minutes a `checks` wait or a `deploy` may take. */
    ciMinutes: 6 * 60,
    /** Triggers on one workflow. */
    triggers: 10,
    approvalMinutes: 30 * 24 * 60,
    /** A script's arguments. */
    args: 20,
    /** Sample answers kept on a draft (one per step), notes on the picture, and the most elements a step runs over (the n8n audit, items A, B, C, E). */
    samples: 50,
    notes: 20,
    eachItems: 50,
    /** Minutes a wait step may wait: a month. */
    waitMinutes: 30 * 24 * 60,
    /** Minutes a step that runs another workflow may wait for it: a day. */
    callMinutes: 24 * 60,
};
// ── What a step may do ─────────────────────────────────────────────────
/**
 * How much a step can change, which is what decides who may approve it (AIOE's roles, mju-aioe/docs/rbac.md section 5):
 * `read` looks only; `change` changes something here (a file, a task, a pull request, a report); `high-impact` is what the
 * organisation's policy names (a delete, money past a budget's warning, an order to a machine, publishing to the whole
 * organisation, another team's resources). A high-impact step runs only after an approval of that class on every path to it.
 */
export const ACTION_CLASSES = ["read", "change", "high-impact"];
export const actionClassSchema = z.enum(ACTION_CLASSES);
export const ACTION_CLASS_WORDS = { read: "Only looks", change: "Changes something", "high-impact": "High impact" };
const rank = (c) => ACTION_CLASSES.indexOf(c);
/** The higher of two classes. */
export const higherClass = (a, b) => (rank(a) >= rank(b) ? a : b);
// ── Names ──────────────────────────────────────────────────────────────
const stepIdSchema = z.string().regex(/^[a-z][a-z0-9-]{0,40}$/, "a step's id is lower-case letters, digits and hyphens, starting with a letter");
/** Ids a workflow may not take: `runs` is a route of its own beside `/workflows/v1/:projectId/:workflowId` (AIOE, 29 Oct 2026). */
export const RESERVED_WORKFLOW_IDS = ["runs"];
const workflowIdSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{0,40}$/, "a workflow's id is lower-case letters, digits and hyphens")
    .refine((id) => !RESERVED_WORKFLOW_IDS.includes(id), "that name is taken by the workbench itself: choose another");
const paramName = z.string().regex(/^[a-z][a-z0-9-]{0,30}$/, "a question's name is lower-case letters, digits and hyphens");
const secretName = z.string().regex(/^[A-Za-z][A-Za-z0-9_.-]{0,99}$/, "a kept key is named, never written out");
const person = z.object({ id: z.string().min(1).max(200), name: z.string().min(1).max(120) }).strict();
// ── Who works an agent step ────────────────────────────────────────────
/**
 * The named agent who works an agent step: a specialist (its id in the catalogue, `specialistId`), or a member of a team by
 * the name the team knows it by. Never "an agent": JP's standing rule is that a flow never starts an unnamed one.
 */
export const workflowMemberSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("specialist"), specialistId: z.string().min(1).max(200), name: z.string().min(1).max(120).optional() }).strict(),
    z.object({ kind: z.literal("member"), teamId: z.string().min(1).max(200), name: z.string().min(1).max(120) }).strict(),
]);
/** In a published workflow, who works a step is asked when it is added: a reference to one of its questions. */
const memberParamSchema = z.object({ param: paramName }).strict();
/**
 * Where an agent or code step works (JP, 29 Oct 2026: "workflows will need to work across repos and possibly projects"):
 * a repository of the workflow's project, by its name in the project, or another project's (a workflow of scope
 * `projects`), by the project's id or name. Either may be a question asked when the workflow is added (`{ param }`, a
 * parameter of kind `repository` or `project`), so a published workflow never names one machine's. Absent: the
 * workflow's own repository (scope `repository`); a project's workflow names one for every step that touches a repository.
 */
export const stepPlaceSchema = z
    .object({
    project: z.union([z.string().trim().min(1).max(200), memberParamSchema]).optional(),
    repository: z.union([z.string().trim().min(1).max(200), memberParamSchema]).optional(),
})
    .strict();
/**
 * The id analytics and audit carry for a member (usage records' and runs' `memberId`): the principal id of AIOE's roles
 * (rbac.md section 5; the workbench's shared/rbac/agentPrincipal.ts). A member of a team is `agent:<teamId>/<id>` by its
 * stable id (D410) when the caller knows it (`opts.id`, the member's id on the team that holds it), else by the name a
 * definition names it by, `agent:<teamId>/<name>`, which is how records before D410 read and still match. A specialist
 * working on its own is `specialist:<specialistId>`. With `memberKind`, which is the union's `kind`.
 */
export function memberIdOf(m, opts = {}) {
    if (m.kind !== "member")
        return specialistAnalyticsId(m.specialistId);
    return agentAnalyticsId(m.teamId, memberKeyOf(opts.id, m.name));
}
// ── Steps ──────────────────────────────────────────────────────────────
const retrySchema = z
    .object({
    /** Tries after the first, when the step fails. */
    attempts: z.number().int().min(0).max(WORKFLOW_LIMITS.retries),
    /** Seconds before the next try; doubled each time. */
    backoffSeconds: z.number().int().min(1).max(3600).default(30),
})
    .strict();
const stepCommon = {
    id: stepIdSchema,
    /** What the step does, in a few words a person reads on the picture. */
    name: z.string().trim().min(1).max(80),
    /** With several branches in: wait for all of them (the default) or start on the first. */
    join: z.enum(["all", "any"]).optional(),
};
/**
 * Run a step once per element of a list (the n8n audit's item C): an earlier step's JSON answer, or a json input, with `path`
 * picking a field inside it (`items`, `data.rows`). Its texts read `{{each.item}}`, `{{each.item.<field>}}` and `{{each.index}}`;
 * its answer is the list of the elements' answers, in order. Bounded: at most `max` elements (default and ceiling
 * {@link WORKFLOW_LIMITS.eachItems}); the rest are left and the answer says so.
 */
export const eachSchema = z
    .object({
    over: z.union([z.object({ step: stepIdSchema, path: z.string().max(200).optional() }).strict(), z.object({ input: paramName, path: z.string().max(200).optional() }).strict()]),
    max: z.number().int().min(1).max(WORKFLOW_LIMITS.eachItems).optional(),
    /** When one element's try fails (after its retries): fail the step (the default), or go on and keep the failure in that element's answer. */
    onFail: z.enum(["fail", "continue"]).optional(),
})
    .strict();
const runnable = {
    retry: retrySchema.optional(),
    /** The longest one try may take; then it fails (and is tried again when `retry` says so). */
    timeoutMinutes: z.number().int().min(1).optional(),
    each: eachSchema.optional(),
};
/** `{{run.inputs.x}}` and `{{steps.<id>.output…}}` in a text: filled from the run's inputs and earlier steps' answers. */
const templated = (max) => z.string().max(max);
/** What an agent step is asked to do about the change that started the run; each has a standing brief (see {@link AGENT_JOB_BRIEFS}). */
export const AGENT_JOBS = ["review-pull-request", "fix-failing-check", "write-tests", "triage-issue"];
export const AGENT_JOB_WORDS = {
    "review-pull-request": "Review the pull request",
    "fix-failing-check": "Fix the failing check",
    "write-tests": "Write tests for the change",
    "triage-issue": "Triage the issue",
};
/** The same sentence the editor shows and the agent is given before the step's own prompt. */
export const AGENT_JOB_BRIEFS = {
    "review-pull-request": "Review this pull request: read the change against its base, say what is wrong or risky, and what is good. Do not change files.",
    "fix-failing-check": "A check failed on this change. Find why from its output, fix it in your worktree and say what you changed.",
    "write-tests": "Write tests for the change that started this run, in the repository's own test style, and make them pass.",
    "triage-issue": "Triage this issue: say what kind it is, how severe, which part of the code it touches and what to ask the reporter. Do not change files.",
};
export const agentStepSchema = z
    .object({
    ...stepCommon,
    kind: z.literal("agent"),
    member: z.union([workflowMemberSchema, memberParamSchema]),
    /** The repository (and project) it works in; {@link stepPlaceSchema}. */
    in: stepPlaceSchema.optional(),
    prompt: templated(WORKFLOW_LIMITS.prompt).pipe(z.string().trim().min(1, "say what the agent is asked")),
    /** D445: what it is asked to do about the change that started the run; its standing brief comes before the prompt. */
    job: z.enum(AGENT_JOBS).optional(),
    /** `text` (the default) or `json`: the agent is asked to answer with one JSON object, which later steps read by field. */
    output: z.enum(["text", "json"]).optional(),
    /** Absent: `change` (an agent can edit its worktree). Never lower. */
    actionClass: actionClassSchema.optional(),
    ...runnable,
})
    .strict();
/**
 * A Foundry agent step (D439, `agentId` and `version` as roadmap D439 names them). One request to the named agent in its
 * project: the answer is the step's output (text, or one JSON object). The agent's own function tools are not run for a
 * step (nothing here works in a repository): an agent that asks for one fails the step with a sentence. It names the agent
 * either by its id in the organisation's catalogue of Foundry agents (`fa_…`, which must be listed and not withdrawn; the
 * step runs the project and agent that entry points to) or by the Foundry reference of one in a project the person works
 * in. A workflow with such a step may be published: AIOE 0.79.0 accepts both.
 */
export const FOUNDRY_CATALOGUE_AGENT_ID = /^fa_[A-Za-z0-9][A-Za-z0-9_.-]{0,99}$/;
/** `<project address>/agents/<agent name>`. */
export const FOUNDRY_REFERENCE_ID = /^https:\/\/\S+\/agents\/[^\s/]+$/i;
export const foundryStepSchema = z
    .object({
    ...stepCommon,
    kind: z.literal("foundry"),
    /** A Foundry step works over the network, in no repository: there is nowhere to say (this keeps the field readable on every step but an approval). */
    in: z.never().optional(),
    /** A catalogue id (`fa_…`) or the Foundry reference `<project address>/agents/<agent name>`. */
    agentId: z
        .string()
        .trim()
        .min(1)
        .max(600)
        .refine((v) => FOUNDRY_CATALOGUE_AGENT_ID.test(v) || FOUNDRY_REFERENCE_ID.test(v), "an agent from your organisation's catalogue, or one of your projects' agents"),
    /** Absent: the agent's newest version when the step runs. */
    version: z.string().trim().min(1).max(100).optional(),
    prompt: templated(WORKFLOW_LIMITS.prompt).pipe(z.string().trim().min(1, "say what the agent is asked")),
    output: z.enum(["text", "json"]).optional(),
    /** Absent: `change` (a hosted tool in Foundry can act). Never lower. */
    actionClass: actionClassSchema.optional(),
    ...runnable,
})
    .strict();
const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
/** The built-in actions a code step may take: a closed table, each run through WorkbenchControl and the tool gate. */
export const codeRunSchema = z.discriminatedUnion("kind", [
    z.discriminatedUnion("action", [
        z
            .object({
            kind: z.literal("action"),
            action: z.literal("http.request"),
            method: z.enum(HTTP_METHODS),
            url: templated(2000),
            /** The name of a key kept in Settings › Secrets, sent as the policy's connector rules say. Never the value. */
            secret: secretName.optional(),
            headers: z.record(z.string().max(100), templated(2000)).optional(),
            body: templated(WORKFLOW_LIMITS.template).optional(),
        })
            .strict(),
        z.object({ kind: z.literal("action"), action: z.literal("file.write"), path: z.string().min(1).max(300), content: templated(WORKFLOW_LIMITS.template) }).strict(),
        z.object({ kind: z.literal("action"), action: z.literal("task.create"), team: z.union([z.string().min(1).max(200), memberParamSchema]).optional(), title: templated(300), body: templated(WORKFLOW_LIMITS.template).optional() }).strict(),
        z.object({ kind: z.literal("action"), action: z.literal("task.comment"), taskId: templated(200), body: templated(WORKFLOW_LIMITS.template) }).strict(),
        z.object({ kind: z.literal("action"), action: z.literal("pull-request.open"), title: templated(300), body: templated(WORKFLOW_LIMITS.template).optional() }).strict(),
        z.object({ kind: z.literal("action"), action: z.literal("inbox.notify"), to: z.enum(["owner", "starter", "project"]), title: templated(300), body: templated(WORKFLOW_LIMITS.brief).optional() }).strict(),
        z.object({ kind: z.literal("action"), action: z.literal("report.publish"), to: z.enum(["library", "team", "organisation"]), title: templated(300), content: templated(WORKFLOW_LIMITS.template) }).strict(),
    ]),
    /** Put earlier answers together: text or JSON from a template, nothing changed. */
    z.object({ kind: z.literal("template"), output: z.enum(["text", "json"]), template: templated(WORKFLOW_LIMITS.template).pipe(z.string().min(1)) }).strict(),
    /** A script in the repository (a path inside it), run in the repository's sandbox; its answer is what it prints. */
    z.object({ kind: z.literal("script"), command: z.string().min(1).max(300), args: z.array(templated(500)).max(WORKFLOW_LIMITS.args).optional() }).strict(),
    /**
     * Code written in the step itself (since 0.90.34): a JavaScript module whose default export is called with the run's scope
     * (`{ inputs, steps: { <id>: { output } }, each }`) and whose answer (a value, or a promise of one) is the step's answer. It
     * runs on this computer in the repository's folder, under the same policy as a script from the repository, with the same
     * stop and size rules; nothing of it is written into the repository.
     */
    z.object({ kind: z.literal("code"), language: z.literal("javascript"), source: z.string().min(1).max(WORKFLOW_LIMITS.source) }).strict(),
]);
export const codeStepSchema = z
    .object({
    ...stepCommon,
    kind: z.literal("code"),
    run: codeRunSchema,
    /** The repository (and project) its file, script, task or pull request is in; {@link stepPlaceSchema}. */
    in: stepPlaceSchema.optional(),
    /** Raises the class the action has of itself ({@link codeRunClass}); never lowers it. */
    actionClass: actionClassSchema.optional(),
    ...runnable,
})
    .strict();
/** Who may decide an approval: the project's owners, its members, or named people. Each needs Act on the workflow. */
const deciderSchema = z.discriminatedUnion("who", [
    z.object({ who: z.literal("owners"), notStarter: z.boolean().optional() }).strict(),
    z.object({ who: z.literal("members"), notStarter: z.boolean().optional() }).strict(),
    z.object({ who: z.literal("people"), people: z.array(person).min(1).max(20), notStarter: z.boolean().optional() }).strict(),
]);
/** An approval that says nothing about a timeout is held after four hours with no answer. */
export const DEFAULT_APPROVAL_TIMEOUT = { minutes: 240, then: "held" };
export const approvalStepSchema = z
    .object({
    ...stepCommon,
    kind: z.literal("approval"),
    /** The class of what it lets through: `high-impact` asks the decider to sign in again within the step-up window. */
    actionClass: actionClassSchema,
    /** What the person reads before they decide (a template over earlier answers). */
    brief: templated(WORKFLOW_LIMITS.brief).optional(),
    decide: deciderSchema.default({ who: "owners" }),
    /** With no answer by then: held or rejected. Never approved. Absent: {@link DEFAULT_APPROVAL_TIMEOUT}. */
    timeout: z
        .object({ minutes: z.number().int().min(5).max(WORKFLOW_LIMITS.approvalMinutes), then: z.enum(["held", "rejected"]) })
        .strict()
        .optional(),
})
    .strict();
// ── Wait, and run another workflow (the n8n audit's item C) ────────────
export const waitUntilSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("minutes"), minutes: z.number().int().min(1).max(WORKFLOW_LIMITS.waitMinutes) }).strict(),
    /** An instant (ISO 8601, or a template over earlier answers that gives one); one already past ends the step at once. */
    z.object({ kind: z.literal("time"), at: templated(64) }).strict(),
    /**
     * Until the run's own address is called (`POST <address>/runs/<runId>/steps/<stepId>`, with the workflow's inbound key):
     * the call's JSON body is the step's answer. With no call in `minutes` (default {@link WORKFLOW_LIMITS.waitMinutes}): the step
     * fails (the default) or goes on with no answer (`then: "continue"`).
     */
    z.object({ kind: z.literal("call"), minutes: z.number().int().min(1).max(WORKFLOW_LIMITS.waitMinutes).optional(), then: z.enum(["continue", "fail"]).optional() }).strict(),
]);
/** The run waits: for a while, until an instant, or until something outside calls the run's address. It only looks. */
export const waitStepSchema = z.object({ ...stepCommon, kind: z.literal("wait"), until: waitUntilSchema }).strict();
/**
 * Another workflow runs as one step (a function with inputs): one on this machine by its id (the repository's, the person's
 * own on it, or a copy added from the catalogue). The step's answer is `{ runId, state, answers }`, the child's final steps'
 * answers by step id. It takes the child's highest class, never lower; a chain of such steps is at most {@link MAX_TRIGGER_CHAIN} deep.
 */
export const callStepSchema = z
    .object({
    ...stepCommon,
    kind: z.literal("workflow"),
    workflowId: workflowIdSchema,
    /** Its inputs by name, each a template over earlier answers (a json input takes the filled text as JSON when it parses). */
    inputs: z.record(paramName, templated(WORKFLOW_LIMITS.template)).optional(),
    actionClass: actionClassSchema.optional(),
    ...runnable,
})
    .strict();
// ── CI steps (D445) ────────────────────────────────────────────────────
/**
 * Where a command runs (D445): on this machine (the repository's sandbox), on a node pool (a node the person's team or
 * organisation placed, by its tag), or on the person's own cluster (D443/D444: a pod per run from the agent sandbox image).
 * Absent: this machine.
 */
export const stepWhereSchema = z.discriminatedUnion("on", [
    z.object({ on: z.literal("machine") }).strict(),
    z.object({ on: z.literal("node-pool"), pool: z.string().trim().min(1).max(100) }).strict(),
    z.object({ on: z.literal("cluster"), cluster: z.string().trim().min(1).max(100), pool: z.string().trim().min(1).max(100).optional() }).strict(),
]);
/** `owner/name`, or a question asked when the workflow is added. Absent: the repository the run's event came from. */
const githubRepositorySchema = z.string().trim().regex(/^[A-Za-z0-9_.-]{1,100}\/[A-Za-z0-9_.-]{1,100}$/, "a GitHub repository is owner/name");
const ciCommon = { ...stepCommon, ...runnable, actionClass: actionClassSchema.optional(), in: stepPlaceSchema.optional() };
/** Get the code at the commit under test into the run's work folder (a ref, else the event's commit, else its branch). */
export const checkoutStepSchema = z
    .object({ ...ciCommon, kind: z.literal("checkout"), ref: templated(300).optional(), depth: z.number().int().min(1).max(1000).optional() })
    .strict();
/** A command in the sandbox: no shell, an argument list. Its answer is what it prints; a non-zero exit fails the step. */
export const runStepSchema = z
    .object({
    ...ciCommon,
    kind: z.literal("run"),
    command: z.string().min(1).max(300),
    args: z.array(templated(500)).max(WORKFLOW_LIMITS.args).optional(),
    where: stepWhereSchema.optional(),
})
    .strict();
/** Wait for the commit's GitHub checks to finish, or read how they stand now. A failing check fails the step unless `failOnFailure` is false. */
export const checksStepSchema = z
    .object({
    ...ciCommon,
    kind: z.literal("checks"),
    mode: z.enum(["wait", "read"]),
    /** Only these checks count (by name); absent, all of them. */
    only: z.array(z.string().trim().min(1).max(200)).max(20).optional(),
    ref: templated(300).optional(),
    failOnFailure: z.boolean().optional(),
    repository: githubRepositorySchema.optional(),
})
    .strict();
/** A comment on the pull request or issue, posted as the workflow's identity. */
export const commentStepSchema = z
    .object({
    ...ciCommon,
    kind: z.literal("comment"),
    on: z.enum(["pull-request", "issue"]).optional(),
    number: templated(20).optional(),
    body: templated(WORKFLOW_LIMITS.template).pipe(z.string().min(1, "say what the comment says")),
    repository: githubRepositorySchema.optional(),
})
    .strict();
/** A review on the pull request. Approving is high impact: a person approves before the workflow does. */
export const reviewStepSchema = z
    .object({
    ...ciCommon,
    kind: z.literal("review"),
    decision: z.enum(["comment", "request-changes", "approve"]),
    number: templated(20).optional(),
    body: templated(WORKFLOW_LIMITS.template).pipe(z.string().min(1, "say what the review says")),
    /** Comments on lines of the change. */
    lines: z.array(z.object({ path: z.string().min(1).max(300), line: z.number().int().min(1), body: templated(2000) }).strict()).max(30).optional(),
    repository: githubRepositorySchema.optional(),
})
    .strict();
/** Merge the pull request. High impact: only through a person's approval. With `requireChecks` (the default) it refuses while a check fails. */
export const mergeStepSchema = z
    .object({
    ...ciCommon,
    kind: z.literal("merge"),
    method: z.enum(["merge", "squash", "rebase"]),
    number: templated(20).optional(),
    requireChecks: z.boolean().optional(),
    repository: githubRepositorySchema.optional(),
})
    .strict();
/**
 * Deploy to a named environment: the approval gate is the rule that a high-impact step is reached only through an approved
 * high-impact approval, so a deploy with no approval before it is not a workflow that can be saved. The named step is a
 * GitHub workflow file dispatched with inputs (and waited for), or a command run where the step says.
 */
export const deployStepSchema = z
    .object({
    ...ciCommon,
    kind: z.literal("deploy"),
    environment: z.string().trim().min(1).max(60),
    via: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("workflow-file"), file: z.string().regex(/^[A-Za-z0-9_.-]{1,100}\.ya?ml$/, "a workflow file in .github/workflows"), ref: templated(200).optional(), inputs: z.record(z.string().max(60), templated(500)).optional() }).strict(),
        z.object({ kind: z.literal("command"), command: z.string().min(1).max(300), args: z.array(templated(500)).max(WORKFLOW_LIMITS.args).optional(), where: stepWhereSchema.optional() }).strict(),
    ]),
    repository: githubRepositorySchema.optional(),
})
    .strict();
export const workflowStepSchema = z.discriminatedUnion("kind", [
    agentStepSchema,
    foundryStepSchema,
    codeStepSchema,
    approvalStepSchema,
    waitStepSchema,
    callStepSchema,
    checkoutStepSchema,
    runStepSchema,
    checksStepSchema,
    commentStepSchema,
    reviewStepSchema,
    mergeStepSchema,
    deployStepSchema,
]);
/** The CI step kinds (D445): proven code the engine runs like a code step, each write through the control layer. */
export const CI_STEP_KINDS = ["checkout", "run", "checks", "comment", "review", "merge", "deploy"];
export const STEP_KINDS = ["agent", "foundry", "code", "approval", "wait", "workflow", ...CI_STEP_KINDS];
/** A CI step: it ends like a code step does (succeeded or failed, tried again when `retry` says so). */
export const isCiStep = (step) => CI_STEP_KINDS.includes(step.kind);
/** A step the engine runs by itself (not an agent's work, not a person's decision): a code step or a CI step. */
export const isMachineStep = (step) => step.kind === "code" || step.kind === "foundry" || isCiStep(step);
export const isWorkingStep = (step) => step.kind !== "approval" && step.kind !== "wait";
/** The place a step names for its files and scripts (`in`). */
export const placeOf = (step) => ("in" in step ? step.in : undefined);
// ── Branches ───────────────────────────────────────────────────────────
/** How a step ended, which decides the branches it takes: an agent or code step succeeded or failed; an approval's decision. */
export const STEP_OUTCOMES = ["succeeded", "failed", "approved", "rejected", "held"];
export const APPROVAL_OUTCOMES = ["approved", "rejected", "held"];
/** A test on the step's answer (a JSON answer, by a dotted path): `severity` equals `high`, `score` greater than 7. */
export const branchTestSchema = z
    .object({
    path: z.string().regex(/^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+){0,7}$/, "a field of the answer, by name (and `.` for one inside it)"),
    op: z.enum(["equals", "not-equals", "contains", "greater-than", "less-than", "exists"]),
    value: z.union([z.string().max(500), z.number(), z.boolean()]).optional(),
})
    .strict();
export const workflowEdgeSchema = z
    .object({
    from: stepIdSchema,
    to: stepIdSchema,
    when: z
        .object({
        /** Absent: `succeeded` for an agent or code step. Required out of an approval. */
        outcome: z.enum(STEP_OUTCOMES).optional(),
        test: branchTestSchema.optional(),
        /** Taken when none of the step's tested branches with this outcome passed. */
        otherwise: z.literal(true).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
// ── Triggers (D445) ────────────────────────────────────────────────────
/** A glob over branch names or file paths: `*` within a part, `**` across parts. Matched by {@link globMatch}. */
const globSchema = z.string().trim().min(1).max(200);
const cronSchema = z
    .string()
    .trim()
    .regex(/^(\S+\s+){4}\S+$/, "a schedule is five fields: minute, hour, day, month, weekday")
    .max(100);
/**
 * What starts a workflow besides a person (JP, 31 Oct 2026: agentic CI/CD is part of Workflows). GitHub's events reach it
 * through a hub node's webhook receiver (no organisation) or the organisation's intake (D446), both as the same
 * {@link WorkflowTriggerEvent}. `repository` limits a GitHub trigger to one repository (`owner/name`); absent, any the
 * delivery is for. A workflow started by one carries what happened as `run.inputs.github` ({@link WorkflowGithubContext}).
 */
export const workflowTriggerSchema = z.discriminatedUnion("on", [
    z.object({ on: z.literal("push"), branches: z.array(globSchema).max(20).optional(), paths: z.array(globSchema).max(20).optional(), repository: githubRepositorySchema.optional() }).strict(),
    z
        .object({
        on: z.literal("pull_request"),
        /** `opened`, `synchronize` (new commits) or `labeled`. Absent: opened and synchronize. */
        actions: z.array(z.enum(["opened", "synchronize", "labeled"])).min(1).max(3).optional(),
        /** With `labeled`: only this label. */
        label: z.string().trim().min(1).max(100).optional(),
        branches: z.array(globSchema).max(20).optional(),
        paths: z.array(globSchema).max(20).optional(),
        repository: githubRepositorySchema.optional(),
    })
        .strict(),
    z.object({ on: z.literal("issue"), actions: z.array(z.enum(["opened", "labeled"])).min(1).max(2).optional(), label: z.string().trim().min(1).max(100).optional(), repository: githubRepositorySchema.optional() }).strict(),
    /** A comment on a pull request or issue that starts with a slash command: `/review`. */
    z.object({ on: z.literal("issue_comment"), command: z.string().regex(/^\/[a-z][a-z0-9-]{0,30}$/, "a slash command like /review"), repository: githubRepositorySchema.optional() }).strict(),
    z.object({ on: z.literal("schedule"), cron: cronSchema, /** An IANA time zone ("Australia/Sydney") the cron is read in; absent, UTC. The same meaning as the organisation's schedule rule's `timeZone`. */ timeZone: z.string().trim().min(1).max(60).optional() }).strict(),
    /** By hand, asking the workflow's `inputs`. */
    z.object({ on: z.literal("manual") }).strict(),
    /** Another workflow ended: any way, or only this one. */
    z.object({ on: z.literal("workflow_completed"), workflowId: workflowIdSchema, outcome: z.enum(["succeeded", "failed", "any"]).optional() }).strict(),
    /**
     * The workflow's own address is called (the n8n audit's item D): `POST <hub or machine>/workflows/v1/<workflowId>/start` with
     * the key as a bearer. `secret` names a kept key the caller must send; absent, the workbench makes one for the address and
     * shows it once. The call's JSON body (at most a run's inputs) is the run's `body` input and `run.event.body`.
     */
    z.object({ on: z.literal("inbound"), secret: secretName.optional() }).strict(),
]);
export const WORKFLOW_TRIGGER_ONS = ["push", "pull_request", "issue", "issue_comment", "schedule", "manual", "workflow_completed", "inbound"];
/** The triggers GitHub's events start. */
export const GITHUB_TRIGGER_ON = ["push", "pull_request", "issue", "issue_comment"];
export const WORKFLOW_TRIGGER_ON_WORDS = {
    push: "Code is pushed",
    pull_request: "A pull request changes",
    issue: "An issue is opened or labelled",
    issue_comment: "Someone comments a command",
    schedule: "On a schedule",
    manual: "By hand",
    workflow_completed: "Another workflow ends",
    inbound: "Its address is called",
};
/** What happened, as a trigger reads it: the same shape whether a hub node, the organisation or the machine's own scheduler delivered it. */
export const workflowTriggerEventSchema = z
    .object({
    on: z.enum(WORKFLOW_TRIGGER_ONS),
    /** `owner/name`, for GitHub's. */
    repository: z.string().max(200).optional(),
    /** `opened`, `synchronize`, `labeled`, `created` (a comment). */
    action: z.string().max(40).optional(),
    branch: z.string().max(250).optional(),
    baseBranch: z.string().max(250).optional(),
    sha: z.string().regex(/^[0-9a-f]{7,64}$/).optional(),
    /** The pull request's or issue's number. */
    number: z.number().int().min(1).optional(),
    /** The files the push or pull request changed (at most 300 are carried). */
    paths: z.array(z.string().max(300)).max(300).optional(),
    label: z.string().max(100).optional(),
    /** The comment's text, for a slash command. */
    comment: z.string().max(4000).optional(),
    actor: z.string().max(100).optional(),
    title: z.string().max(300).optional(),
    url: z.string().url().max(2000).optional(),
    /** GitHub's delivery id (or the schedule's slot): the same one twice starts one run. */
    deliveryId: z.string().max(100).optional(),
    workflowId: z.string().max(100).optional(),
    outcome: z.enum(["succeeded", "failed"]).optional(),
    /** How many workflows' endings led here (a workflow that starts on another's end): at most {@link MAX_TRIGGER_CHAIN}. */
    depth: z.number().int().min(0).max(10).optional(),
    /** A call to the workflow's address: its JSON body (the receiver keeps it to a run's inputs' size) and who the key belonged to. */
    body: z.unknown().optional(),
    caller: z.string().max(100).optional(),
    at: z.string().min(1).max(64),
})
    .strict();
export const MAX_TRIGGER_CHAIN = 3;
/**
 * What a run started by GitHub carries as `run.inputs.github`, and what a CI step reads by default. When the organisation
 * delivers an event (AIOE's webhook intake, D447) it sends the usual `workflow.start` order with `inputs: { github }` in this
 * shape and the matching workflow's id: no second order kind, so a hub node and the organisation start the same run.
 */
export const workflowGithubContextSchema = workflowTriggerEventSchema.omit({ on: true, at: true }).extend({ event: z.enum(WORKFLOW_TRIGGER_ONS) }).strict();
/** The context of an event, as `run.inputs.github`. */
export function githubContextOf(e) {
    const { on, at: _at, ...rest } = e;
    return { event: on, ...rest };
}
/** The five cron fields' values (minute, hour, day, month, weekday) of an instant in a time zone; undefined for a zone that does not exist. */
function zoned(d, timeZone) {
    try {
        // date-ok: the cron fields of an instant in a time zone, read for matching, never shown to anyone
        const parts = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", minute: "numeric", hour: "numeric", day: "numeric", month: "numeric", weekday: "short" }).formatToParts(d);
        const get = (t) => parts.find((p) => p.type === t)?.value ?? "";
        const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
        return [Number(get("minute")), Number(get("hour")), Number(get("day")), Number(get("month")), dow];
    }
    catch {
        return undefined;
    }
}
/** Whether a five-field cron (minute hour day month weekday; a star, a star-slash-n step, a-b ranges, lists) is due in the minute `d` falls in, read in `timeZone` (default UTC). */
export function cronMatches(cron, d, timeZone = "UTC") {
    const f = cron.trim().split(/\s+/);
    if (f.length !== 5)
        return false;
    const vals = zoned(d, timeZone);
    if (!vals)
        return false;
    const ranges = [[0, 59], [0, 23], [1, 31], [1, 12], [0, 7]];
    const field = (spec, v, [lo, hi], weekday) => spec.split(",").some((part) => {
        const [range = "", stepText] = part.split("/");
        const step = stepText === undefined ? 1 : Number(stepText);
        if (!Number.isInteger(step) || step < 1)
            return false;
        let a = lo;
        let b = hi;
        if (range !== "*") {
            const [x, y] = range.split("-");
            a = Number(x);
            b = y === undefined ? (stepText === undefined ? a : hi) : Number(y);
            if (!Number.isInteger(a) || !Number.isInteger(b))
                return false;
        }
        const hit = (n) => n >= a && n <= b && (n - a) % step === 0;
        return hit(v) || (weekday && v === 0 && hit(7));
    });
    return vals.every((v, i) => field(f[i], v, ranges[i], i === 4));
}
/** A glob over a branch name or path: `*` stays inside a part, `**` crosses parts. */
export function globMatch(pattern, value) {
    const re = pattern
        .split("**")
        .map((chunk) => chunk.split("*").map((p) => p.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join("[^/]*"))
        .join(".*");
    return new RegExp(`^${re}$`).test(value);
}
/**
 * The first of a workflow's triggers an event fits, or undefined. A push or pull request with `paths` fits only when
 * some changed file matches one (an event that carries no paths fits, since nothing says it does not). Pure.
 */
export function matchingWorkflowTrigger(triggers, e) {
    for (const t of triggers ?? []) {
        if (t.on !== e.on)
            continue;
        if ("repository" in t && t.repository && e.repository && t.repository.toLowerCase() !== e.repository.toLowerCase())
            continue;
        if (t.on === "push" || t.on === "pull_request") {
            const branch = t.on === "push" ? e.branch : (e.baseBranch ?? e.branch);
            if (t.branches?.length && !(branch !== undefined && t.branches.some((g) => globMatch(g, branch))))
                continue;
            if (t.paths?.length && e.paths?.length && !e.paths.some((p) => t.paths?.some((g) => globMatch(g, p))))
                continue;
        }
        if (t.on === "pull_request") {
            const actions = t.actions ?? ["opened", "synchronize"];
            if (!e.action || !actions.includes(e.action))
                continue;
            if (e.action === "labeled" && t.label && t.label.toLowerCase() !== (e.label ?? "").toLowerCase())
                continue;
        }
        if (t.on === "issue") {
            const actions = t.actions ?? ["opened"];
            if (!e.action || !actions.includes(e.action))
                continue;
            if (e.action === "labeled" && t.label && t.label.toLowerCase() !== (e.label ?? "").toLowerCase())
                continue;
        }
        if (t.on === "issue_comment") {
            const first = (e.comment ?? "").trim().split(/\s+/)[0] ?? "";
            if (first.toLowerCase() !== t.command.toLowerCase())
                continue;
        }
        if (t.on === "schedule" && !cronMatches(t.cron, new Date(e.at), t.timeZone ?? "UTC"))
            continue;
        if ((e.depth ?? 0) > MAX_TRIGGER_CHAIN)
            continue;
        if (t.on === "workflow_completed") {
            if (t.workflowId !== e.workflowId)
                continue;
            if (t.outcome && t.outcome !== "any" && t.outcome !== e.outcome)
                continue;
        }
        return t;
    }
    return undefined;
}
/** A sentence a person reads about one trigger. */
export function describeWorkflowTrigger(t) {
    const list = (xs) => (xs?.length ? xs.join(", ") : "");
    switch (t.on) {
        case "push":
            return `Code is pushed${t.branches?.length ? ` to ${list(t.branches)}` : ""}${t.paths?.length ? ` touching ${list(t.paths)}` : ""}`;
        case "pull_request": {
            const acts = (t.actions ?? ["opened", "synchronize"]).map((a) => (a === "synchronize" ? "gets new commits" : a === "labeled" ? `is labelled${t.label ? ` ${t.label}` : ""}` : "is opened"));
            return `A pull request ${acts.join(" or ")}`;
        }
        case "issue":
            return `An issue ${(t.actions ?? ["opened"]).map((a) => (a === "labeled" ? `is labelled${t.label ? ` ${t.label}` : ""}` : "is opened")).join(" or ")}`;
        case "issue_comment":
            return `Someone comments ${t.command}`;
        case "schedule":
            return `On a schedule (${t.cron})`;
        case "manual":
            return "By hand";
        case "workflow_completed":
            return `${t.workflowId} ends${t.outcome && t.outcome !== "any" ? ` ${t.outcome === "failed" ? "in failure" : "well"}` : ""}`;
        case "inbound":
            return `Its address is called${t.secret ? ` with the key ${t.secret}` : ""}`;
    }
}
/** The run's event, as a sentence and a place to look, in the shape every run carries (shared/schemas/routineTriggers.ts). */
export function runEventOf(e) {
    const where = e.repository ? ` in ${e.repository}` : "";
    const what = e.on === "push"
        ? `Push to ${e.branch ?? "a branch"}${where}`
        : e.on === "pull_request"
            ? `Pull request #${e.number ?? "?"} ${e.action ?? "changed"}${where}${e.title ? `: ${e.title}` : ""}`
            : e.on === "issue"
                ? `Issue #${e.number ?? "?"} ${e.action ?? "changed"}${where}${e.title ? `: ${e.title}` : ""}`
                : e.on === "issue_comment"
                    ? `${e.actor ?? "Someone"} commented ${(e.comment ?? "").trim().split(/\s+/)[0] ?? ""} on #${e.number ?? "?"}${where}`
                    : e.on === "workflow_completed"
                        ? `${e.workflowId ?? "A workflow"} ended${e.outcome ? ` (${e.outcome})` : ""}`
                        : e.on === "schedule"
                            ? "The schedule came due"
                            : e.on === "inbound"
                                ? `Its address was called${e.caller ? ` by ${e.caller}` : ""}`
                                : "Started by hand";
    return { source: e.on === "schedule" || e.on === "workflow_completed" || e.on === "manual" || e.on === "inbound" ? "workflow" : "github", kind: e.action ? `${e.on}.${e.action}` : e.on, summary: what.slice(0, 300), ...(e.url ? { url: e.url } : {}), at: e.at, ...(e.branch ? { branch: e.branch } : {}), ...(e.label ? { label: e.label } : {}) };
}
// ── The definition ─────────────────────────────────────────────────────
export const workflowInputSchema = z
    .object({
    name: paramName,
    /** The question a person answers when they start it by hand. */
    label: z.string().min(1).max(80),
    type: z.enum(["text", "number", "boolean", "json"]),
    required: z.boolean(),
})
    .strict();
/** What the person who adds a published workflow is asked: who works its agent steps, which team takes its tasks. */
export const workflowParameterSchema = z
    .object({ name: paramName, kind: z.enum(["specialist", "member", "team", "repository", "project"]), label: z.string().min(1).max(80), required: z.boolean() })
    .strict();
export const workflowDefinitionSchema = z
    .object({
    schema: z.literal(WORKFLOW_SCHEMA_VERSION),
    id: workflowIdSchema,
    name: z.string().trim().min(1).max(80),
    purpose: z.string().trim().min(1).max(300),
    /** `major.minor.patch`; a published version is immutable. */
    version: z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/),
    /**
     * One repository's (it runs in that repository's workbench), the whole project's (on the desktop, or a node: its steps
     * work in the project's repositories), or one that spans projects (on the desktop or a node that holds them all: its
     * steps name their project too).
     */
    scope: z.enum(["repository", "project", "projects"]),
    /** The person accountable for its runs: named in every audit row a run writes when no person started it. */
    owner: person.optional(),
    inputs: z.array(workflowInputSchema).max(WORKFLOW_LIMITS.inputs).default([]),
    parameters: z.array(workflowParameterSchema).max(8).optional(),
    /** D445: what starts it besides a person. Absent: only by hand, a routine or the organisation's order, as before. */
    triggers: z.array(workflowTriggerSchema).max(WORKFLOW_LIMITS.triggers).optional(),
    steps: z.array(workflowStepSchema).min(1, "a workflow has at least one step").max(WORKFLOW_LIMITS.steps),
    edges: z.array(workflowEdgeSchema).max(WORKFLOW_LIMITS.edges).default([]),
    /**
     * A draft's sample answer per step (the n8n audit's items A and B): kept from a run, pasted, or written; what "Try this
     * step" feeds the steps before it with, and where the Insert menu reads a step's fields from. Each at most a step's answer's
     * size ({@link workflowProblems}). Never in a published version: `publishShape` drops them.
     */
    samples: z.record(stepIdSchema, z.unknown()).optional(),
    /** Notes on the picture (item E): a sentence a person leaves near a step, or anywhere. Not steps; they run nothing. */
    notes: z
        .array(z.object({ id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,40}$/, "a note's id is lower-case letters, digits and hyphens"), text: z.string().trim().min(1).max(1000), near: stepIdSchema.optional() }).strict())
        .max(WORKFLOW_LIMITS.notes)
        .optional(),
    /** Item I: this repository's specialists and team members may start it as a tool (`workflow_start`), its inputs the tool's arguments. */
    offerAsTool: z.boolean().optional(),
})
    .strict();
const zodSentence = (issue) => {
    const where = issue.path.join(".");
    return /^Invalid|^Too|^Expected|^Unrecognized/.test(issue.message) ? `${where || "the workflow"}: ${issue.message.toLowerCase()}` : issue.message;
};
/** A workflow as saved or received: its shape, then {@link workflowProblems}. Each problem is a sentence a person can act on. */
export function parseWorkflow(raw) {
    const shaped = workflowDefinitionSchema.safeParse(raw);
    if (!shaped.success)
        return { ok: false, errors: shaped.error.issues.slice(0, 10).map(zodSentence) };
    const problemList = workflowProblemList(shaped.data);
    return problemList.length ? { ok: false, errors: problemList.map((p) => p.text), problemList } : { ok: true, workflow: shaped.data };
}
/** The class a code step's work has of itself: a template or a GET only looks; publishing to the whole organisation is high impact. */
export function codeRunClass(run) {
    if (run.kind === "template")
        return "read";
    if (run.kind === "script" || run.kind === "code")
        return "change";
    if (run.action === "http.request")
        return run.method === "GET" ? "read" : "change";
    if (run.action === "report.publish" && run.to === "organisation")
        return "high-impact";
    return "change";
}
/** The class a CI step has of itself: reading checks or checking out only looks; merging, deploying and approving a review are high impact. */
export function ciStepClass(step) {
    switch (step.kind) {
        case "checkout":
        case "checks":
            return "read";
        case "run":
        case "comment":
            return "change";
        case "review":
            return step.decision === "approve" ? "high-impact" : "change";
        case "merge":
        case "deploy":
            return "high-impact";
    }
}
/** The least a step may say about itself. An agent may be kept to looking (its tools are then read-only). */
function floorClass(step) {
    if (step.kind === "agent" || step.kind === "foundry" || step.kind === "wait")
        return "read";
    if (step.kind === "workflow")
        return "change";
    return step.kind === "code" ? codeRunClass(step.run) : ciStepClass(step);
}
/** What a step can change, as the engine and the approval rules take it: what it says, never below what it does. */
export function stepClass(step) {
    if (step.kind === "approval")
        return step.actionClass;
    const floor = floorClass(step);
    const said = "actionClass" in step ? step.actionClass : undefined;
    return higherClass(floor, said ?? (step.kind === "agent" || step.kind === "foundry" ? "change" : floor));
}
/** The decision a branch follows: what it says, else `succeeded` out of an agent or code step. */
export function edgeOutcome(e) {
    return e.when?.outcome;
}
const OUTCOME_AFTER = { succeeded: "it succeeded", failed: "it failed", approved: "it was approved", rejected: "it was rejected", held: "it was held" };
const CLASS_DOES = { read: "only looks", change: "changes something", "high-impact": "is high impact" };
const CLASS_SAYS = { read: "only looks", change: "only changes something", "high-impact": "is high impact" };
const orWords = (xs) => (xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} or ${xs[xs.length - 1]}`);
/** A path inside the repository: relative, with no way up and out of it. */
function insideRepository(p) {
    const s = p.replace(/\\/g, "/");
    if (s.startsWith("/") || /^[A-Za-z]:/.test(s) || s.startsWith("~"))
        return false;
    return !s.split("/").some((part) => part === "..");
}
/** Every text in a step, for its `{{…}}` references. */
function textsOf(v, out = []) {
    if (typeof v === "string")
        out.push(v);
    else if (Array.isArray(v))
        for (const x of v)
            textsOf(x, out);
    else if (v && typeof v === "object")
        for (const x of Object.values(v))
            textsOf(x, out);
    return out;
}
const REFERENCE_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;
/** The steps that come before each step (every step some path leads from), for a picture with no loop. */
function ancestorsOf(order, into) {
    const out = new Map();
    for (const id of order) {
        const set = new Set();
        for (const from of into.get(id) ?? []) {
            set.add(from);
            for (const a of out.get(from) ?? [])
                set.add(a);
        }
        out.set(id, set);
    }
    return out;
}
/** One loop in the picture, as the steps on it from where it starts back to there, or undefined. */
function findLoop(ids, out) {
    const colour = new Map();
    const stack = [];
    const visit = (id) => {
        colour.set(id, 1);
        stack.push(id);
        for (const next of out.get(id) ?? []) {
            if (colour.get(next) === 1)
                return [...stack.slice(stack.indexOf(next)), next];
            if (!colour.has(next)) {
                const found = visit(next);
                if (found)
                    return found;
            }
        }
        stack.pop();
        colour.set(id, 2);
        return undefined;
    };
    for (const id of ids) {
        if (colour.has(id))
            continue;
        const found = visit(id);
        if (found)
            return found;
    }
    return undefined;
}
/** What is wrong with a well-shaped workflow's picture, as sentences. Empty when it may be saved and run. */
/** Whether a step works in a repository: an agent step, a script, and the file, task and pull-request actions. */
export function touchesRepository(step) {
    if (step.kind === "agent" || step.kind === "checkout" || step.kind === "run")
        return true;
    if (step.kind !== "code")
        return false;
    if (step.run.kind === "script" || step.run.kind === "code")
        return true;
    return step.run.kind === "action" && ["file.write", "task.create", "task.comment", "pull-request.open"].includes(step.run.action);
}
export function workflowProblems(wf) {
    return workflowProblemList(wf).map((p) => p.text);
}
/** The same problems as {@link workflowProblems}, each with its step's id, so a card is never picked by a name that repeats. */
export function workflowProblemList(wf) {
    const problems = [];
    const add = (text, stepId) => void problems.push({ text, ...(stepId ? { stepId } : {}) });
    const byId = new Map();
    for (const s of wf.steps) {
        if (byId.has(s.id))
            add(`Two steps are called ${s.id}: give each its own.`, s.id);
        else
            byId.set(s.id, s);
    }
    const named = (id) => byId.get(id)?.name ?? id;
    // Branches between steps that are there.
    const edges = [];
    for (const e of wf.edges) {
        const from = byId.has(e.from);
        const to = byId.has(e.to);
        if (from && !to)
            add(`A branch from ${named(e.from)} goes to a step that is not there (${e.to}).`, e.from);
        else if (!from && to)
            add(`A branch into ${named(e.to)} comes from a step that is not there (${e.from}).`, e.to);
        else if (!from && !to)
            add(`A branch joins two steps that are not there (${e.from} and ${e.to}).`);
        else if (e.from === e.to)
            add(`${named(e.from)} has a branch back to itself.`, e.from);
        else
            edges.push(e);
    }
    // The decision each branch follows, and every way out of an approval.
    const otherwise = new Map();
    for (const e of edges) {
        const from = byId.get(e.from);
        const outcome = edgeOutcome(e);
        const isDecision = outcome !== undefined && APPROVAL_OUTCOMES.includes(outcome);
        if (from.kind === "approval" && !isDecision)
            add(`The branch from ${from.name} to ${named(e.to)} does not say which decision it follows.`, e.from);
        else if (from.kind !== "approval" && isDecision)
            add(`${from.name} is not an approval, so its branch to ${named(e.to)} cannot follow ${outcome}.`, e.from);
        if (e.when?.otherwise) {
            const key = `${e.from}\u0000${outcome ?? "succeeded"}`;
            otherwise.set(key, (otherwise.get(key) ?? 0) + 1);
        }
    }
    for (const [key, count] of otherwise) {
        if (count < 2)
            continue;
        const [from, outcome] = key.split("\u0000");
        add(`${named(from)} has more than one branch for when nothing else fits after ${OUTCOME_AFTER[outcome]}.`, from);
    }
    for (const s of wf.steps) {
        if (s.kind !== "approval")
            continue;
        const covered = new Set(edges.filter((e) => e.from === s.id).map(edgeOutcome));
        const missing = APPROVAL_OUTCOMES.filter((o) => !covered.has(o));
        if (missing.length)
            add(`${s.name} does not say what happens when it is ${orWords(missing)}.`, s.id);
    }
    for (const t of wf.triggers ?? [])
        if (t.on === "workflow_completed" && t.workflowId === wf.id)
            add("It starts when itself ends: that never stops. Name another workflow.");
    // A project's workflow started by a GitHub event names the repository: every repository's events would reach it otherwise.
    if (wf.scope !== "repository")
        for (const t of wf.triggers ?? [])
            if (GITHUB_TRIGGER_ON.includes(t.on) && !("repository" in t && t.repository))
                add(`${WORKFLOW_TRIGGER_ON_WORDS[t.on]} starts it, so say which GitHub repository (owner/name): a ${wf.scope === "project" ? "project's" : "workflow spanning projects"} cannot tell which one is meant.`);
    // The questions a published workflow asks when it is added: each one used, and none used that it does not ask.
    const asked = new Set((wf.parameters ?? []).map((p) => p.name));
    const used = new Set();
    for (const s of wf.steps) {
        const param = s.kind === "agent" && "param" in s.member ? s.member.param : s.kind === "code" && s.run.kind === "action" && s.run.action === "task.create" && typeof s.run.team === "object" ? s.run.team.param : undefined;
        if (param === undefined)
            continue;
        used.add(param);
        if (!asked.has(param))
            add(`${s.name} asks for ${param}, which the workflow does not ask when it is added.`, s.id);
    }
    // A step's place may be a question too (checked against its kind with the places, below).
    for (const s of wf.steps) {
        if (s.kind === "approval")
            continue;
        for (const v of [placeOf(s)?.project, placeOf(s)?.repository])
            if (v && typeof v === "object")
                used.add(v.param);
    }
    for (const p of wf.parameters ?? [])
        if (!used.has(p.name))
            add(`It asks "${p.label}" when it is added, but no step uses the answer.`);
    // What each step says it can change, and where its files and scripts are.
    for (const s of wf.steps) {
        if (!isWorkingStep(s))
            continue;
        const floor = floorClass(s);
        if (s.actionClass && rank(s.actionClass) < rank(floor))
            add(`${s.name} ${CLASS_DOES[floor]}, so it cannot say it ${CLASS_SAYS[s.actionClass]}.`, s.id);
        if (s.timeoutMinutes !== undefined) {
            const most = s.kind === "agent" ? WORKFLOW_LIMITS.agentMinutes : s.kind === "workflow" ? WORKFLOW_LIMITS.callMinutes : s.kind === "checks" || s.kind === "deploy" ? WORKFLOW_LIMITS.ciMinutes : WORKFLOW_LIMITS.codeMinutes;
            if (s.timeoutMinutes > most)
                add(`${s.name} may take at most ${most} minutes a try.`, s.id);
        }
        if (s.kind === "code" && s.run.kind === "action" && s.run.action === "file.write" && !insideRepository(s.run.path))
            add(`${s.name} writes outside the repository (${s.run.path}).`, s.id);
        if (s.kind === "code" && s.run.kind === "script" && !insideRepository(s.run.command))
            add(`${s.name} runs something outside the repository (${s.run.command}).`, s.id);
        if (s.kind === "run" && /[\\/]/.test(s.command) && !insideRepository(s.command))
            add(`${s.name} runs something outside the repository (${s.command}).`, s.id);
        if (s.kind === "deploy" && s.via.kind === "command" && /[\\/]/.test(s.via.command) && !insideRepository(s.via.command))
            add(`${s.name} runs something outside the repository (${s.via.command}).`, s.id);
        if (s.kind === "deploy" && s.via.kind === "workflow-file" && s.via.file.includes(".."))
            add(`${s.name} names a workflow file outside .github/workflows.`, s.id);
    }
    // Samples and notes belong to steps that are there; a sample is no bigger than an answer may be; no workflow runs itself.
    for (const [id, sample] of Object.entries(wf.samples ?? {})) {
        if (!byId.has(id))
            add(`A sample answer is kept for a step that is not there (${id}).`);
        else if (jsonBytes(sample) > WORKFLOW_LIMITS.outputBytes)
            add(`The sample answer kept for ${named(id)} is bigger than ${Math.round(WORKFLOW_LIMITS.outputBytes / 1000)} KB.`, id);
    }
    if (Object.keys(wf.samples ?? {}).length > WORKFLOW_LIMITS.samples)
        add(`It keeps more than ${WORKFLOW_LIMITS.samples} sample answers.`);
    for (const n of wf.notes ?? [])
        if (n.near && !byId.has(n.near))
            add(`A note is placed near a step that is not there (${n.near}).`);
    for (const s of wf.steps)
        if (s.kind === "workflow" && s.workflowId === wf.id)
            add(`${s.name} runs this same workflow: that never stops. Name another workflow.`, s.id);
    // The picture runs forward only.
    const outOf = new Map();
    const into = new Map();
    for (const e of edges) {
        outOf.set(e.from, [...(outOf.get(e.from) ?? []), e.to]);
        into.set(e.to, [...(into.get(e.to) ?? []), e.from]);
    }
    const ids = [...byId.keys()];
    const loop = findLoop(ids, outOf);
    if (loop) {
        add(`The way ${loop.map(named).join(" → ")} goes round in a loop: a workflow only runs forward.`, loop[0]);
        return problems;
    }
    const order = [];
    const seen = new Set();
    const place = (id) => {
        if (seen.has(id))
            return;
        seen.add(id);
        for (const from of into.get(id) ?? [])
            place(from);
        order.push(id);
    };
    for (const id of ids)
        place(id);
    const before = ancestorsOf(order, into);
    // A step reads only the run's inputs and the answers of steps before it.
    const inputs = new Set(wf.inputs.map((i) => i.name));
    // A workflow started by GitHub carries what happened as `run.inputs.github` (see {@link WorkflowGithubContext}).
    if ((wf.triggers ?? []).some((t) => GITHUB_TRIGGER_ON.includes(t.on)))
        inputs.add("github");
    // A workflow whose address is called carries the call's body as `run.inputs.body`.
    if ((wf.triggers ?? []).some((t) => t.on === "inbound"))
        inputs.add("body");
    // A step that runs over a list reads it from a step before it, or from a json input.
    for (const s of wf.steps) {
        if (!isWorkingStep(s) || !s.each)
            continue;
        const over = s.each.over;
        if ("step" in over) {
            if (!byId.has(over.step))
                add(`${s.name} runs over the answer of a step that is not there (${over.step}).`, s.id);
            else if (!before.get(s.id)?.has(over.step))
                add(`${s.name} runs over the answer of ${named(over.step)}, which does not come before it.`, s.id);
        }
        else if (!inputs.has(over.input))
            add(`${s.name} runs over an input the workflow does not ask for (${over.input}).`, s.id);
    }
    for (const s of wf.steps) {
        const said = new Set();
        for (const text of textsOf(s)) {
            for (const m of text.matchAll(REFERENCE_RE)) {
                const parts = m[1].split(".");
                let problem;
                if (parts[0] === "steps") {
                    const ref = parts[1] ?? "";
                    if (!byId.has(ref))
                        problem = `${s.name} reads the answer of a step that is not there (${ref}).`;
                    else if (!before.get(s.id)?.has(ref))
                        problem = `${s.name} reads the answer of ${named(ref)}, which does not come before it.`;
                }
                else if (parts[0] === "run" && parts[1] === "inputs") {
                    if (!inputs.has(parts[2] ?? ""))
                        problem = `${s.name} reads an input the workflow does not ask for (${parts[2] ?? ""}).`;
                }
                else if (parts[0] === "each") {
                    if (!(isWorkingStep(s) && s.each))
                        problem = `${s.name} uses {{${m[1]}}}, but it does not run over a list.`;
                    else if (!["item", "index"].includes(parts[1] ?? ""))
                        problem = `${s.name} uses {{${m[1]}}}: a step running over a list reads {{each.item}} and {{each.index}}.`;
                }
                else if (!(parts[0] === "run" && ["id", "event", "started"].includes(parts[1] ?? ""))) {
                    problem = `${s.name} uses {{${m[1]}}}, which is not an input or an earlier step's answer.`;
                }
                if (problem && !said.has(problem)) {
                    said.add(problem);
                    add(problem, s.id);
                }
            }
        }
    }
    // Humans approve: a high-impact step is reached only through the approved way out of a high-impact approval.
    const reached = new Set();
    const queue = ids.filter((id) => !(into.get(id)?.length));
    while (queue.length) {
        const id = queue.shift();
        if (reached.has(id))
            continue;
        reached.add(id);
        const s = byId.get(id);
        const guards = s.kind === "approval" && s.actionClass === "high-impact";
        for (const e of edges)
            if (e.from === id && !(guards && edgeOutcome(e) === "approved"))
                queue.push(e.to);
    }
    for (const s of wf.steps) {
        if (s.kind !== "approval" && stepClass(s) === "high-impact" && reached.has(s.id))
            add(`${s.name} is high impact: put an approval of high impact before it on every way to it.`, s.id);
    }
    // Where each step works: a repository's workflow stays in it; a project's names the repository; one spanning projects names both.
    const params = new Map((wf.parameters ?? []).map((p) => [p.name, p.kind]));
    for (const s of wf.steps) {
        if (s.kind === "approval" || s.kind === "wait")
            continue;
        const place = placeOf(s);
        for (const [field, kind] of [["project", "project"], ["repository", "repository"]]) {
            const v = place?.[field];
            if (v && typeof v === "object" && params.get(v.param) !== kind)
                add(`${s.name} asks “${v.param}” for its ${field}, which is not one of the workflow's ${kind} questions.`, s.id);
        }
        const touches = touchesRepository(s);
        if (wf.scope === "repository" && (place?.project || place?.repository))
            add(`${s.name} works in another repository or project: make the workflow a project's, or one that spans projects, to reach it.`, s.id);
        else if (wf.scope === "project" && place?.project)
            add(`${s.name} is in another project: make the workflow one that spans projects.`, s.id);
        else if (wf.scope === "project" && touches && !place?.repository)
            add(`Say which repository ${s.name} works in.`, s.id);
        else if (wf.scope === "projects" && touches && (!place?.project || !place?.repository))
            add(`Say which project and repository ${s.name} works in.`, s.id);
    }
    return problems;
}
// ── States ─────────────────────────────────────────────────────────────
/**
 * A step's state, in order: queued < running < waiting < (succeeded | failed | cancelled | skipped). A state only moves
 * forward; the four ends are final.
 */
export const WORKFLOW_STATES = ["queued", "running", "waiting", "succeeded", "failed", "cancelled", "skipped"];
export const workflowStateSchema = z.enum(WORKFLOW_STATES);
/** The four ends. */
export const FINAL_STATES = ["succeeded", "failed", "cancelled", "skipped"];
export const isFinal = (s) => FINAL_STATES.includes(s);
const stateRank = (s) => Math.min(WORKFLOW_STATES.indexOf(s), 3);
/** Whether a record in `from` may move to `to`: only forward, never out of an end, and the same state is no move. */
export function mayAdvance(from, to) {
    return stateRank(to) > stateRank(from);
}
/**
 * Of the record held and one just reported, the one to keep. Both sides apply it to every report of a run or a step, so a
 * report sent twice changes nothing and one that arrives late never moves a record back: a later state wins, and within one
 * state the higher `rev` (the machine counts each report of a record up from 1).
 */
export function keepReport(held, reported) {
    if (!held)
        return reported;
    if (held.id !== reported.id)
        throw new Error(`keepReport compares reports of one record: ${held.id} is not ${reported.id}`);
    if (mayAdvance(held.state, reported.state))
        return reported;
    if (held.state === reported.state && reported.rev > held.rev)
        return reported;
    return held;
}
// ── Run and step records ───────────────────────────────────────────────
/** What started a run. */
export const WORKFLOW_TRIGGER_KINDS = ["manual", "routine", "event", "team", "specialist", "organisation", "workflow", "inbound"];
/** Whether the agent behind a usage record, a run or a step is a specialist on its own or a member of a team. */
export const MEMBER_KINDS = ["specialist", "member"];
const iso = z.string().min(1).max(64);
const jsonBytes = (v) => {
    try {
        return new TextEncoder().encode(JSON.stringify(v) ?? "").length;
    }
    catch {
        return Number.POSITIVE_INFINITY;
    }
};
const boundedJson = (max, what) => z.unknown().refine((v) => jsonBytes(v) <= max, `${what} may be at most ${Math.round(max / 1000)} KB`);
const runIdSchema = z.string().regex(/^wfr_[A-Za-z0-9_-]{4,80}$/, "a run's id starts wfr_");
/** A run's id from the order that started it: the same order received twice starts one run. */
export function runIdForOrder(orderId) {
    return `wfr_${orderId.replace(/[^A-Za-z0-9_-]/g, "-").slice(0, 80)}`;
}
/** A run's id from a schedule occurrence's batch and the workflow: the same batch twice finds the run it started. */
export function runIdForBatch(batchId, workflowId) {
    return `wfr_${`${batchId}-${workflowId}`.replace(/[^A-Za-z0-9_-]/g, "-").slice(-80)}`;
}
/**
 * A step record's id: one per step per try, so a retry is a new record and no record ever goes back to queued. A step that
 * runs over a list has one record per element too (`:<index>` on the end), beside the step's own record that gathers them.
 */
export function stepRecordId(runId, stepId, attempt, item) {
    return `${runId}:${stepId}:${attempt}${item === undefined ? "" : `:${item}`}`;
}
const triggerSchema = z
    .object({
    kind: z.enum(WORKFLOW_TRIGGER_KINDS),
    /** The person who started it, when one did. */
    by: person.optional(),
    routineId: z.string().max(200).optional(),
    teamId: z.string().max(200).optional(),
    specialistId: z.string().max(200).optional(),
    /** The organisation's `workflow.start` order. */
    orderId: z.string().max(100).optional(),
    /** What happened, for a run started by an event (shared/schemas/routineTriggers.ts). */
    event: routineEventSchema.omit({ outside: true }).optional(),
})
    .strict();
/**
 * One run of a workflow, as the machine running it keeps it and reports it to the project's home. A run's own state is queued,
 * running or an end: it waits through its steps, and `waitingOn` names the approvals that hold it ("Waiting for you").
 */
export const workflowRunSchema = z
    .object({
    id: runIdSchema,
    /** Counted up from 1 by the machine at each report of this record ({@link keepReport}). */
    rev: z.number().int().min(1),
    workflowId: workflowIdSchema,
    version: z.string().max(20),
    /** The definition's hash, for a published one. */
    hash: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    projectId: z.string().max(100).optional(),
    rootId: z.string().max(200).optional(),
    /** Where it runs: a repository's workbench, the desktop (a project's), a node. */
    where: z.enum(["repository", "desktop", "node"]),
    deviceId: z.string().max(100).optional(),
    state: z.enum(["queued", "running", "succeeded", "failed", "cancelled"]),
    trigger: triggerSchema,
    /** The accountable person: who started it, else the workflow's owner. Named in every audit row of the run. */
    owner: person,
    inputs: boundedJson(WORKFLOW_LIMITS.inputBytes, "a run's inputs").pipe(z.record(z.string(), z.unknown())),
    waitingOn: z.array(z.object({ stepId: stepIdSchema, since: iso }).strict()).max(WORKFLOW_LIMITS.steps).optional(),
    createdAt: iso,
    startedAt: iso.optional(),
    endedAt: iso.optional(),
    /** Why it failed or was cancelled, in a sentence. */
    note: z.string().max(500).optional(),
    /** Started again from an earlier run (item A): the whole of it, or from one step, with the answers before that step carried over. */
    rerunOf: z.object({ runId: runIdSchema, fromStep: stepIdSchema.optional() }).strict().optional(),
    /** Started by a `workflow` step of another run (item C): the parent run and the step that waits for this one. */
    parent: z.object({ runId: runIdSchema, stepId: stepIdSchema, attempt: z.number().int().min(1) }).strict().optional(),
})
    .strict();
/** One try of one step. An approval that was decided ends `succeeded` with the decision as its outcome. */
export const workflowStepRunSchema = z
    .object({
    /** {@link stepRecordId}. */
    id: z.string().min(1).max(200),
    runId: runIdSchema,
    stepId: stepIdSchema,
    attempt: z.number().int().min(1).max(WORKFLOW_LIMITS.retries + 1),
    rev: z.number().int().min(1),
    kind: z.enum(STEP_KINDS),
    state: workflowStateSchema,
    /** At an end: succeeded or failed, or an approval's decision. It picks the branches taken. */
    outcome: z.enum(STEP_OUTCOMES).optional(),
    queuedAt: iso,
    /** A retry waits until then. */
    notBefore: iso.optional(),
    startedAt: iso.optional(),
    endedAt: iso.optional(),
    durationMs: z.number().int().nonnegative().optional(),
    /** The step's answer, as later steps read it. */
    output: boundedJson(WORKFLOW_LIMITS.outputBytes, "a step's answer").optional(),
    /** Why it failed, in a sentence. */
    error: z.string().max(500).optional(),
    /** Where it worked: the project and repository its place named, resolved (a workflow spanning repositories or projects). */
    place: z.object({ projectId: z.string().max(100).optional(), rootId: z.string().max(200).optional(), repository: z.string().max(200).optional() }).strict().optional(),
    /** An agent step: who worked it, and the session and run that did (agentRuns.ts `runId`). */
    agent: z
        .object({ memberId: z.string().min(1).max(300), memberKind: z.enum(MEMBER_KINDS), sessionId: z.string().max(200).optional(), agentRunId: z.string().max(200).optional() })
        .strict()
        .optional(),
    /** An approval: who decided and why, or that the timeout decided. */
    approval: z
        .object({ decidedBy: person.optional(), decidedAt: iso.optional(), reason: z.string().max(1000).optional(), timedOut: z.literal(true).optional(), notificationId: z.string().max(200).optional() })
        .strict()
        .optional(),
    /** One element of a step that runs over a list (item C): which, of how many. The step's own record (no `item`) gathers the answers. */
    item: z.object({ index: z.number().int().min(0), of: z.number().int().min(1) }).strict().optional(),
    /** A `workflow` step: the run it started. A `wait` step until a call: the address it waits at (the key never). */
    child: z.object({ runId: runIdSchema }).strict().optional(),
    waitingAt: z.string().max(500).optional(),
    /** Carried over from an earlier run by "run again from here" (item A): the record it was copied from. */
    carriedFrom: z.string().max(200).optional(),
})
    .strict()
    .superRefine((r, ctx) => {
    if (r.id !== stepRecordId(r.runId, r.stepId, r.attempt, r.item?.index))
        ctx.addIssue({ code: "custom", path: ["id"], message: "a step record's id is its run, its step and its try" });
    const decision = r.outcome !== undefined && APPROVAL_OUTCOMES.includes(r.outcome);
    if (r.kind === "approval" && r.state === "succeeded" && !decision)
        ctx.addIssue({ code: "custom", path: ["outcome"], message: "an approval that ended says what was decided" });
    if (r.kind !== "approval" && decision)
        ctx.addIssue({ code: "custom", path: ["outcome"], message: "only an approval ends approved, rejected or held" });
    if (r.kind !== "approval" && r.outcome && r.state !== r.outcome)
        ctx.addIssue({ code: "custom", path: ["outcome"], message: "a step's outcome is how it ended" });
});
/**
 * The answer of the project home's `GET /workflows/v1/:projectId/runs` (AIOE, and a hub): the runs, newest first, with their
 * step records. `truncated` when there were more than the answer holds.
 */
export const workflowRunsResponseSchema = z.object({ runs: z.array(workflowRunSchema).max(500), steps: z.array(workflowStepRunSchema).max(5000), truncated: z.boolean() }).strict();
// ── Starting a run from the organisation ───────────────────────────────
export const WORKFLOW_START_ORDER_KIND = "workflow.start";
/**
 * The body of the organisation's `workflow.start` device order (on the heartbeat's `orders`, shared/schemas/deviceOrders.ts):
 * start this workflow here. The run's id is {@link runIdForOrder} of the order's id. Answered `done` ("Started Security incident
 * response") or `refused` with a code of {@link WORKFLOW_START_REFUSALS}.
 */
export const workflowStartOrderBodySchema = z
    .object({
    /** The published workflow's id, or a workflow's own id on this machine. */
    workflowId: workflowIdSchema,
    /** A published version; absent, the newest held here. */
    version: z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/).optional(),
    projectId: z.string().max(100).optional(),
    rootId: z.string().max(200).optional(),
    inputs: boundedJson(WORKFLOW_LIMITS.inputBytes, "a run's inputs").pipe(z.record(z.string(), z.unknown())).default({}),
    event: routineEventSchema.omit({ outside: true }).optional(),
    /** The person the organisation starts it for (their principal); absent, the workflow's owner is accountable. */
    by: z.string().max(200).optional(),
    /** D447: a schedule rule's occurrence (`schedule:<ruleId>:<ISO>`): the same batch twice is already started ({@link runIdForBatch}). */
    batchId: z.string().min(1).max(200).optional(),
})
    .strict();
/**
 * `no_workflow` (none here by that id and version), `not_allowed` (the policy's workflows clause), `invalid_inputs` (an input
 * missing or of the wrong kind), `no_member` (a specialist or member it names is not on this machine), `rate_limited`.
 */
export const WORKFLOW_START_REFUSALS = ["no_workflow", "not_allowed", "invalid_inputs", "no_member", "rate_limited", "no_run", "too_deep"];
/**
 * A run's inputs, with what an event can answer filled in. A run the organisation starts for an event arrives with `inputs: {}`
 * and the `event`; an input it does not carry is taken from the event when it names one of the event's fields (`summary`,
 * `url`, `source`, `kind`, `at`, `branch`, `label`, `alert`; a text input), or is named `event` (a json input: the whole
 * event). What the order carries always stands. A required input still missing after this is refused `invalid_inputs`.
 */
export function inputsWithEvent(inputs, given, event) {
    const out = { ...given };
    if (!event)
        return out;
    for (const input of inputs) {
        if (out[input.name] !== undefined)
            continue;
        if (input.name === "event" && input.type === "json")
            out.event = event;
        else if (input.name === "body" && input.type === "json" && event.body !== undefined)
            out.body = event.body;
        else if (input.type === "text" && EVENT_FIELDS.includes(input.name) && typeof event[input.name] === "string")
            out[input.name] = event[input.name];
    }
    return out;
}
const EVENT_FIELDS = ["summary", "url", "source", "kind", "at", "branch", "label", "alert"];
/** The required inputs a run would still lack: empty when it may start. */
export function missingInputs(inputs, given) {
    return inputs.filter((i) => i.required && (given[i.name] === undefined || given[i.name] === "")).map((i) => i.name);
}
/**
 * `facts.workflows` on a heartbeat or registration (shared/schemas/deviceOrders.ts, beside `routines.outside`): the workflows
 * this machine holds and may start, so the organisation sends `workflow.start` only to a machine that can run it. Absent until
 * the machine reports it; an empty list holds none.
 */
export const workflowFactsSchema = z.object({ held: z.array(z.object({ id: workflowIdSchema, versions: z.array(z.string().max(20)).max(20) }).strict()).max(200) }).strict();
// ── Deciding an approval ───────────────────────────────────────────────
/**
 * The Control API method on the machine running the workflow that the Inbox's Approve, Reject and Hold call (the organisation
 * relays a phone's answer to it). Refused with `already_decided`, `not_decider`, `own_run` (a step that says the person who
 * started the run may not decide it) or, for a high-impact approval outside the step-up window, `sign_in_again`.
 */
export const WORKFLOW_APPROVAL_DECIDE_RPC = "workflows.approval.decide";
/**
 * Where a refusal's code is: the JSON-RPC error's `data.code` (one of {@link WORKFLOW_ERRORS}'s keys), with the sentence a
 * person reads as its `message`. AIOE reads `error.data.code`, then `error.data.error`, then `error.message`.
 */
export const workflowRefusal = (code) => ({ message: WORKFLOW_ERRORS[code], data: { code } });
export const workflowApprovalDecisionSchema = z
    .object({
    runId: runIdSchema,
    stepId: stepIdSchema,
    attempt: z.number().int().min(1),
    decision: z.enum(APPROVAL_OUTCOMES),
    reason: z.string().max(1000).optional(),
})
    .superRefine((d, ctx) => {
    if (d.decision !== "approved" && !d.reason?.trim())
        ctx.addIssue({ code: "custom", path: ["reason"], message: d.decision === "rejected" ? "say why it is rejected" : "say why it is held" });
});
/** The Inbox record of an approval, one per step of a run: kind `approval`, needs-you. */
export const workflowApprovalDedupeKey = (runId, stepId) => `workflow-approval:${runId}/${stepId}`;
/** The Inbox's three buttons. Reject and Hold ask why before they send (as Decline does, D379). */
export function workflowApprovalActions(p) {
    const at = (decision) => ({ runId: p.runId, stepId: p.stepId, attempt: p.attempt, decision });
    return [
        { id: "approve", label: "Approve", rpc: WORKFLOW_APPROVAL_DECIDE_RPC, params: at("approved") },
        { id: "reject", label: "Reject", rpc: WORKFLOW_APPROVAL_DECIDE_RPC, params: at("rejected") },
        { id: "hold", label: "Hold", rpc: WORKFLOW_APPROVAL_DECIDE_RPC, params: at("held") },
    ];
}
// ── The catalogue ──────────────────────────────────────────────────────
/** Where a workflow lives: yours, the repository's (`.mojoup/workflows/`), your team's hub, your organisation. */
export const WORKFLOW_WHERES = ["mine", "repository", "team", "organisation"];
export const WORKFLOW_WHERE_WORDS = { mine: "Mine", repository: "This repository", team: "Your team", organisation: "Your organisation" };
/**
 * What a workflow does, as a reader judges it before adding it: its steps by kind, its highest class, the kept keys it needs.
 * A manifest carries the counts of its own kinds (`workflowManifestSchema`), so a Foundry step is not counted there; its
 * class still counts toward `highest`.
 */
export function workflowNeeds(wf) {
    const steps = { agent: 0, code: 0, approval: 0 };
    let highest = "read";
    const secrets = new Set();
    let scripts = false;
    for (const s of wf.steps) {
        if (isCiStep(s))
            steps.ci = (steps.ci ?? 0) + 1;
        else if (s.kind === "agent" || s.kind === "code" || s.kind === "approval")
            steps[s.kind]++;
        // A wait step and a workflow step count as code: proven steps the engine runs by itself.
        else if (s.kind === "wait" || s.kind === "workflow")
            steps.code++;
        highest = higherClass(highest, stepClass(s));
        if (s.kind === "code" && s.run.kind === "action" && s.run.action === "http.request" && s.run.secret)
            secrets.add(s.run.secret);
        // Code written in the step runs on the computer like a script, so the organisation's policy sees it as one.
        if (s.kind === "code" && (s.run.kind === "script" || s.run.kind === "code"))
            scripts = true;
        // A command in the sandbox is a script as far as the organisation's policy is concerned.
        if (s.kind === "run" || (s.kind === "deploy" && s.via.kind === "command"))
            scripts = true;
    }
    return { steps, highest, secrets: [...secrets].sort(), scripts };
}
/** A version as published: the definition with who works its agent steps asked when it is added, and the facts beside it. */
export const workflowManifestSchema = z
    .object({
    schema: z.literal(WORKFLOW_SCHEMA_VERSION),
    id: workflowIdSchema,
    name: z.string().min(1).max(80),
    purpose: z.string().min(1).max(300),
    version: z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/),
    author: person,
    /** {@link workflowNeeds}, checked against the definition. */
    steps: z.object({ agent: z.number().int().min(0), code: z.number().int().min(0), approval: z.number().int().min(0), ci: z.number().int().min(1).optional() }).strict(),
    highest: actionClassSchema,
    secrets: z.array(secretName).max(20),
    scripts: z.boolean(),
    definition: workflowDefinitionSchema,
    /** SHA-256 of {@link canonicalWorkflowManifest}. */
    hash: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    publishedAt: z.string().max(40).optional(),
    withdrawnAt: z.string().max(40).optional(),
})
    .strict();
/** The manifest without `hash`, `publishedAt` and `withdrawnAt`, keys sorted: what is hashed and compared. */
export function canonicalWorkflowManifest(m) {
    const { hash: _h, publishedAt: _p, withdrawnAt: _w, ...rest } = m;
    const sort = (v) => {
        if (Array.isArray(v))
            return v.map(sort);
        if (v && typeof v === "object")
            return Object.fromEntries(Object.entries(v).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([k, x]) => [k, sort(x)]));
        return v;
    };
    return JSON.stringify(sort(rest));
}
/** A version offered for publishing: its shape, its definition's rules, no machine's member, team, repository or project named, its facts true. */
export function parseWorkflowManifest(raw) {
    const shaped = workflowManifestSchema.safeParse(raw);
    if (!shaped.success)
        return { ok: false, errors: shaped.error.issues.slice(0, 10).map(zodSentence) };
    const m = shaped.data;
    const errors = [...workflowProblems(m.definition)];
    const d = m.definition;
    if (m.id !== d.id || m.name !== d.name || m.version !== d.version || m.purpose !== d.purpose)
        errors.push("Its name, id, version or purpose differ from what it runs.");
    for (const s of d.steps)
        if (s.kind === "agent" && !("param" in s.member))
            errors.push(`${s.name} names who works it: ask for them when it is added instead.`);
    for (const s of d.steps)
        if (s.kind === "code" && s.run.kind === "action" && s.run.action === "task.create" && typeof s.run.team === "string")
            errors.push(`${s.name} names the team that takes its task: ask for it when it is added instead.`);
    for (const s of d.steps)
        if (s.kind !== "approval" && (typeof placeOf(s)?.project === "string" || typeof placeOf(s)?.repository === "string"))
            errors.push(`${s.name} names the repository or project it works in: ask for it when it is added instead.`);
    if (canonicalWorkflowManifest({ ...m, ...workflowNeeds(d) }) !== canonicalWorkflowManifest(m))
        errors.push("What it says it does does not match its steps.");
    return errors.length ? { ok: false, errors } : { ok: true, manifest: m };
}
export const WORKFLOW_LISTING_STATUSES = ["listed", "pending", "rejected", "withdrawn"];
export const WORKFLOW_LISTING_STATUS_WORDS = { listed: "Listed", pending: "Waiting for review", rejected: "Not accepted", withdrawn: "Withdrawn" };
export const workflowPublishRequestSchema = z.object({ manifest: workflowManifestSchema }).strict();
export const workflowReviewDecisionSchema = z.object({ decision: z.enum(["approve", "reject"]), reason: z.string().max(400).optional() }).strict();
/** A pending version is an Inbox record for the project's owners (kind `decision`), as an app's and a routine's are. */
export const WORKFLOW_REVIEW_DECIDE_RPC = "aioe.workflows.review.decide";
export const workflowReviewDedupeKey = (workflowId, version) => `workflow-review:${workflowId}@${version}`;
/** Counting an add (`POST …/:workflowId/versions/:v/install`): the hash the person saw, so a version that moved is `changed`. */
export const workflowInstallRequestSchema = z.object({ hash: z.string().regex(/^[0-9a-f]{64}$/).optional() }).strict();
/**
 * What a copy added from the catalogue keeps of where it came from (phase 5): the home's listing it was added at, so the
 * Workflows view can say a newer version is listed. The copy itself is an ordinary workflow, its questions answered.
 */
export const workflowCatalogueLinkSchema = z
    .object({
    where: z.enum(["team", "organisation"]),
    workflowId: workflowIdSchema,
    version: z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/),
    hash: z.string().regex(/^[0-9a-f]{64}$/),
    addedAt: z.string().max(40),
})
    .strict();
// ── The policy clause ──────────────────────────────────────────────────
/**
 * The `workflows` clause (workspace policy, `.mojoup/policy.json`, D401 phase 5): whether workflows run here, how they are
 * shared through the team's or the organisation's catalogue, and where a code step of kind `script` may run. Left out: they
 * run; publishing is listed at once; a repository's own workflows run its scripts; a published workflow with a script is
 * not listed or added (off by default at the organisation); any class up to high impact.
 */
export const workflowsPolicySchema = z.object({
    enabled: z.boolean().optional().describe("Whether workflows may run here at all. Absent: true."),
    publishing: z.enum(["anyone", "review", "off"]).optional().describe("What happens when a person publishes a workflow to the team or organisation: anyone (listed at once), review (an owner approves each version first), off. Absent: anyone."),
    scripts: z.enum(["repository", "off"]).optional().describe("Where a code step that runs a script from the repository may run: repository (a repository's own workflows run its scripts) or off (no workflow runs a script). Absent: repository."),
    allowScripts: z.boolean().optional().describe("Whether a published workflow with a script step may be listed, added and run here. Absent: false."),
    maxClass: actionClassSchema.optional().describe("The highest class a workflow may have here (read, change, high-impact): one above it is not published, added or started. Absent: high-impact."),
});
/** The clause with its defaults filled in. `scripts: "off"` also turns `allowScripts` off: no script runs here. */
export function effectiveWorkflowsPolicy(p) {
    const scripts = p?.scripts ?? "repository";
    return { enabled: p?.enabled ?? true, publishing: p?.publishing ?? "anyone", scripts, allowScripts: scripts !== "off" && (p?.allowScripts ?? false), maxClass: p?.maxClass ?? "high-impact" };
}
/**
 * Why the policy would not let this workflow be published, added or started, as a refusal code; undefined when it may.
 * `published`: it came from (or goes to) a catalogue, so a script step needs `allowScripts`; a repository's own needs only
 * `scripts` not to be off.
 */
export function workflowPolicyRefusal(policy, needs, published) {
    if (!policy.enabled)
        return { error: "not_allowed", detail: "workflows are off here" };
    if (needs.scripts && (policy.scripts === "off" || (published && !policy.allowScripts)))
        return { error: "scripts_not_allowed" };
    if (rank(needs.highest) > rank(policy.maxClass))
        return { error: "not_allowed", detail: `it is ${ACTION_CLASS_WORDS[needs.highest].toLowerCase()}; at most ${ACTION_CLASS_WORDS[policy.maxClass].toLowerCase()} runs here` };
    return undefined;
}
/** The clause in a sentence for the Workspace policy card. */
export function describeWorkflowsPolicy(p) {
    const e = effectiveWorkflowsPolicy(p);
    if (!e.enabled)
        return "Workflows: off";
    const pub = e.publishing === "off" ? "publishing workflows is off" : e.publishing === "review" ? "published workflows wait for an owner's review" : "published workflows are listed at once";
    const scripts = e.scripts === "off" ? "no workflow runs a script" : e.allowScripts ? "shared workflows may run scripts too" : "only a repository's own workflows run its scripts";
    return `Workflows: ${pub}; ${scripts}${e.maxClass === "high-impact" ? "" : `; at most ${ACTION_CLASS_WORDS[e.maxClass].toLowerCase()}`}`;
}
// ── Words ──────────────────────────────────────────────────────────────
export const STEP_KIND_WORDS = {
    agent: "A specialist or team member works on it",
    foundry: "A Microsoft Foundry agent answers it",
    code: "Proven code does it",
    approval: "A person approves",
    wait: "The run waits: a while, until an instant, or for a call to its address",
    workflow: "Another workflow runs, and its answer comes back",
    checkout: "Get the code at the commit under test",
    run: "Run a command in the sandbox",
    checks: "Wait for, or read, the GitHub checks",
    comment: "Comment on the pull request or issue",
    review: "Review the pull request",
    merge: "Merge the pull request (after an approval)",
    deploy: "Deploy to an environment (after an approval)",
};
export const STATE_WORDS = {
    queued: "Waiting to start",
    running: "Running",
    waiting: "Waiting for a person",
    succeeded: "Done",
    failed: "Failed",
    cancelled: "Cancelled",
    skipped: "Not needed",
};
export const OUTCOME_WORDS = { succeeded: "Done", failed: "Failed", approved: "Approved", rejected: "Rejected", held: "Held" };
export const TRIGGER_WORDS = {
    manual: "Started by hand",
    routine: "Started by a routine",
    event: "Started when something happened",
    team: "Started by a team",
    specialist: "Started by a specialist",
    organisation: "Started by your organisation",
    workflow: "Started by another workflow",
    inbound: "Started by a call to its address",
};
/** A refusal's code, and the sentence a person reads. */
export const WORKFLOW_ERRORS = {
    no_workflow: "That workflow is not on this computer.",
    not_allowed: "Your organisation's policy does not let this workflow run here.",
    invalid_inputs: "It was started without what it needs: say which incident, ticket or item it is for.",
    no_member: "A specialist or team member it asks for is not on this computer.",
    rate_limited: "It has already started as often as it may this hour.",
    already_decided: "This approval was already decided.",
    not_decider: "You are not one of the people who may decide this.",
    own_run: "You started this run, so someone else decides it.",
    sign_in_again: "This is high impact: sign in again, then decide.",
    publishing_off: "Your organisation does not let people publish workflows.",
    scripts_not_allowed: "Your organisation shares only workflows without scripts from a repository.",
    version_exists: "That version is already published. Publish a newer one.",
    taken: "Someone else already shares a workflow with that name. Give yours another name.",
    own_workflow: "You cannot approve your own workflow.",
    changed: "This workflow changed since it was listed. Look at it again, then add it.",
    not_listed: "That version is not offered any more.",
    not_reviewer: "Only an owner of the project can review this.",
    invalid: "This workflow cannot run as it is drawn.",
    no_run: "That run is not on this computer.",
    too_deep: "Workflows running workflows go only three deep.",
    not_tryable: "Only a code, CI, wait or workflow step can be tried on its own: an agent step is a task, and an approval a decision.",
    no_sample: "A step before it has no sample answer yet: keep one from a run, or write one.",
    not_waiting: "That step is not waiting for a call.",
    bad_address: "The address or its key is wrong.",
    no_proposal: "That proposal is not here any more.",
};
//# sourceMappingURL=workflows.js.map